/**
 * Eigenbau: Die Schiene des Entwurfs — 236px, dauerhaft dunkel, ohne Symbole.
 *
 * Ersetzt `ui/navigation/Sidebar.tsx` (Tremor-Block „template-insights"). Der
 * Block bleibt liegen, bis entschieden ist, ob er weg kann.
 *
 * Was der Entwurf anders will als die Vorlage — jeder Punkt ist ein Grund, den
 * Block nicht weiter zu verbiegen:
 *
 * - **Keine Symbole.** Der Entwurf führt die Navigation allein über Schrift.
 *   Die Vorlage baut auf `lucide`-Symbole je Punkt.
 * - **Drei benannte Gruppen** mit Mono-Titeln statt einer flachen Liste.
 * - **Zählwerte rechts** an jedem Punkt.
 * - **2px Akzentkante** am aktiven Punkt statt einer gefüllten Fläche.
 * - **Kein Einklappen.** Der Entwurf zeigt keinen Umschalter; die Breite ist
 *   fest. Damit entfällt auch der Grund für die Tooltips der Vorlage.
 *
 * Die Schiene bleibt in **beiden** Themen dunkel und benutzt deshalb die
 * Tafel-Tinte, nicht `--tinte*` — siehe `entwurf/Tafel`.
 */

import { useEffect } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { cx, focusRing } from "@/lib/utils";
import { Wortmarke } from "@/components/Wortmarke";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { siteConfig } from "@/siteConfig";
import {
  useAufgabe,
  useIch,
  useNavigationszahlen,
  usePerson,
  useProjekte,
} from "@/lib/haken";
import { ALLE_PROJEKTE, useProjektAuswahl } from "@/lib/projekt-auswahl";
import {
  projektIdAusPfad,
  waehleProjektkontext,
  zielNachProjektwechsel,
} from "@/lib/projektkontext";
import { Kontextwaehler } from "@/components/entwurf/Kontextwaehler";
import { DropdownUserProfile } from "@/components/ui/navigation/DropdownUserProfile";
import type { Rolle } from "@cubic/shared";

/** Ein Punkt der Navigation. `zahl` bleibt offen, solange sie nicht belegt ist. */
type Punkt = {
  label: string;
  ziel: string;
  /**
   * Die Zahl aus `navigation.zaehler`. `null` heißt: Dieser Punkt trägt keine
   * Zahl (Heute, Puls, Einstellungen) oder sie gilt nur mit Projekt.
   */
  zahl?: number | null;
  /** Trifft auch Unterseiten, etwa `/aufgaben/CUB-142`. */
  praefix?: boolean;
  /**
   * Eine Zahl, die auf jemanden wartet: Sie steht im Akzent, sobald sie über
   * null liegt. Der Akzent ist im Entwurf für Handlung und Überfälligkeit
   * reserviert — ein offener Merge Request ist beides, eine Seitenzahl nicht.
   * Bei null bleibt der Punkt ruhig; sonst behauptete eine Null Alarm.
   */
  akzentAbEins?: boolean;
  /**
   * Eine Abkürzung, kein Ort: Der Punkt führt auf eine Seite, die schon einem
   * anderen Punkt gehört, und trägt deshalb nie die Aktivkante. Sonst stünden
   * auf der Lieferung — und auf „Heute" — zwei Kanten nebeneinander, und der
   * Entwurf hat für „hier bin ich" genau eine.
   */
  ohneAktivmarke?: boolean;
};

type Gruppe = { titel: string; punkte: readonly Punkt[] };

/** Initialen aus einem Namen — höchstens zwei, wie im Entwurf. */
function initialen(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return "?";
  const ersteZwei = teile.slice(0, 2);
  return ersteZwei.map((t) => t[0]!.toUpperCase()).join("");
}

const ROLLENNAME: Readonly<Record<Rolle, string>> = {
  gast: "Gast",
  mitglied: "Mitglied",
  betreuer: "Betreuer",
  verwalter: "Verwalter",
};

export function Schiene({ className }: { className?: string }) {
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  const suche = useRouterState({ select: (s) => s.location.search }) as Record<
    string,
    unknown
  >;
  const navigate = useNavigate();
  const projekte = useProjekte();
  const ich = useIch();
  const person = usePerson(ich.data?.personId);
  const { auswahl, setzeAuswahl } = useProjektAuswahl();

  // Übernommen aus der Vorlage: Eine Auswahl, die es nicht mehr gibt, fällt
  // auf „Alle Projekte" zurück — sonst zeigt die Schiene ein Projekt an, das
  // niemand mehr sehen darf.
  useEffect(() => {
    if (
      projekte.data &&
      auswahl !== ALLE_PROJEKTE &&
      !projekte.data.some((projekt) => projekt.id === auswahl)
    ) {
      setzeAuswahl(ALLE_PROJEKTE);
    }
  }, [auswahl, projekte.data, setzeAuswahl]);

  /*
   * Welches Projekt die Gruppe führt, entscheidet `waehleProjektkontext` —
   * Auswahl, dann Adresse, dann das erste sichtbare Projekt. Die Begründung der
   * drei Stufen steht dort, samt der zwei Regressionen, die sie behebt.
   */
  const alleProjekte = projekte.data ?? [];
  const gewaehlt = waehleProjektkontext({
    auswahl,
    alleWert: ALLE_PROJEKTE,
    projekte: alleProjekte,
    pfad,
  });

  // Eine Abfrage für alle sechs Zahlen — nicht sechs Listen je Seitenwechsel.
  const zahlen = useNavigationszahlen(gewaehlt?.id);

  /*
   * Die gerade offene Aufgabe, für den Punkt „CUB-142" der Vorlage. Die
   * Kennung steht nicht in der Adresse — dort steht die Kennung als UUID —,
   * also muss die Aufgabe gelesen werden. Sie liegt beim Öffnen der
   * Detailseite ohnehin im Zwischenspeicher, die Abfrage kostet hier nichts.
   */
  const aufgabeId = /^\/aufgaben\/([^/]+)$/.exec(pfad)?.[1];
  const aufgabe = useAufgabe(aufgabeId);
  const offeneAufgabe =
    aufgabeId && aufgabe.data
      ? {
          kennung: `${
            (projekte.data ?? []).find((p) => p.id === aufgabe.data?.projektId)
              ?.kuerzel ?? "—"
          }-${aufgabe.data.nummer}`,
          ziel: `/aufgaben/${aufgabeId}`,
        }
      : undefined;

  /*
   * Der Wechsel behält die Seite, wo es sie im neuen Projekt gibt: Wer den
   * Puls liest und das Projekt wechselt, will dessen Puls sehen. Wohin genau,
   * entscheidet zielNachProjektwechsel; ein leeres Ergebnis heißt „hier ist nichts zu
   * tun", und die Seite bleibt einfach stehen.
   *
   * „Alle Projekte" ist der eine Fall, der das nicht kann: Eine Projektseite
   * ohne Projekt gibt es nicht. Von dort führt der Weg auf die Projektliste —
   * die Ebene, die „alle" tatsächlich zeigt.
   */
  const waehleProjekt = (wert: string) => {
    setzeAuswahl(wert);

    if (wert === ALLE_PROJEKTE) {
      if (projektIdAusPfad(pfad)) void navigate({ to: "/projekte" });
      return;
    }

    const ziel = zielNachProjektwechsel(pfad, wert);
    if (ziel) void navigate({ to: ziel });
  };

  const gruppen: readonly Gruppe[] = [
    {
      titel: "Arbeit",
      punkte: [
        { label: "Heute", ziel: "/" },
        {
          label: "Aufgaben",
          ziel: siteConfig.baseLinks.aufgaben,
          praefix: true,
          zahl: zahlen.data?.aufgaben,
        },
        {
          label: "Klärungen",
          ziel: siteConfig.baseLinks.klaerungen,
          zahl: zahlen.data?.klaerungen,
        },
        /*
         * „Projekte" führt die Vorlage nicht — sie zeigt in der Schiene immer
         * ein bereits gewähltes Projekt („Projekt CUB") und kennt den Zustand
         * „Alle Projekte" nicht.
         *
         * Der Eintrag muss trotzdem stehen: `/projekte` ist die einzige Stelle,
         * an der ein Projekt **angelegt** wird (Knopf „Neues Projekt"), und der
         * Kontextschalter kann nur zwischen bestehenden wechseln. Ohne diesen
         * Punkt war die Seite allein über die getippte Adresse erreichbar — wer
         * noch kein Projekt hat, kam nirgends hin.
         */
        { label: "Projekte", ziel: siteConfig.baseLinks.projekte },
        /*
         * Merge Requests stehen in der Arbeitsgruppe, nicht nur beim Projekt:
         * Die Projektgruppe erscheint erst mit einem gewählten Projekt, und
         * bei „Alle Projekte" blieb ein wartender Merge Request damit
         * unsichtbar, bis jemand das richtige Projekt anwählte.
         *
         * Das Ziel folgt derselben Auswahl: mit Projekt dessen Lieferung, ohne
         * Projekt „Heute" — dort steht das Band mit einer Zeile je Projekt.
         */
        {
          label: "Merge Requests",
          ziel: gewaehlt ? `/projekte/${gewaehlt.id}?reiter=lieferung` : "/",
          zahl: zahlen.data?.mergeRequests,
          akzentAbEins: true,
          ohneAktivmarke: true,
        },
      ],
    },
    /*
     * Die Projektgruppe erscheint nur mit einem konkreten Projekt. Bei „Alle
     * Projekte" hätten Puls, Lieferung und Seiten kein Ziel — der Entwurf
     * schreibt die Gruppe auf ein Projekt („Projekt CUB").
     *
     * Die Ziele gehen weiter über `?reiter=`, weil genau diese Adressen heute
     * bestehen. Eigene Pfade je Bereich kommen mit den Bereichen selbst; bis
     * dahin bleibt die Reiterleiste auf dem Projektdetail stehen — sie trägt
     * vier Reiter (Durchsicht, Regeln, Personen, Aufgaben), die die Schiene
     * nicht zeigt, und sie vorher zu entfernen würde sie unerreichbar machen.
     */
    ...(gewaehlt
      ? [
          {
            titel: `Projekt ${gewaehlt.kuerzel ?? ""}`.trim(),
            punkte: [
              {
                label: "Übersicht",
                ziel: `/projekte/${gewaehlt.id}?reiter=uebersicht`,
              },
              { label: "Puls", ziel: `/projekte/${gewaehlt.id}?reiter=puls` },
              {
                label: "Meilensteine",
                ziel: `/projekte/${gewaehlt.id}?reiter=meilensteine`,
                zahl: zahlen.data?.meilensteine,
              },
              /*
               * Auto Q&A steht in der Vorlage zwischen Meilensteinen und
               * Lieferung. Seit Paket 19 führt die Route auf die echte
               * Übersicht — Aufträge, Aufhalter und Automationen aus
               * `autoQa.*`, nicht mehr auf „Entwurf offen".
               */
              { label: "Auto Q&A", ziel: "/auto-qa", praefix: true },
              {
                label: "Lieferung",
                ziel: `/projekte/${gewaehlt.id}?reiter=lieferung`,
                zahl: zahlen.data?.lieferung,
                akzentAbEins: true,
              },
              {
                label: "Seiten",
                ziel: `/projekte/${gewaehlt.id}/seiten`,
                praefix: true,
                zahl: zahlen.data?.seiten,
              },
              /*
               * Die offene Aufgabe steht als eigener Punkt in der Gruppe — der
               * Entwurf führt dort `CUB-142`. Sie erscheint nur, solange eine
               * Aufgabe offen ist, und trägt ihre Kennung, nicht ihren Titel:
               * Auf 236px wäre der Titel ohnehin abgeschnitten, die Kennung ist
               * die Auskunft.
               */
              ...(offeneAufgabe
                ? [{ label: offeneAufgabe.kennung, ziel: offeneAufgabe.ziel }]
                : []),
              /*
               * Zuletzt in der Gruppe: Was ein Projekt verwaltet, statt in ihm
               * zu arbeiten. Steht unter der Arbeit, nicht zwischen ihr.
               */
              {
                label: "Einstellungen",
                ziel: `/projekte/${gewaehlt.id}?reiter=einstellungen`,
              },
            ],
          } satisfies Gruppe,
        ]
      : []),
    {
      titel: "System",
      punkte: [
        {
          label: "Einstellungen",
          ziel: siteConfig.baseLinks.einstellungen.persoenlich,
        },
        {
          label: "Metarr",
          ziel: siteConfig.baseLinks.einstellungen.metarr,
          zahl: zahlen.data?.metarr,
        },
      ],
    },
  ];

  /**
   * Aktiv ist ein Punkt, wenn seine Adresse die aktuelle ist — bei `praefix`
   * auch für Unterseiten. Reiterziele vergleichen zusätzlich den Reiter, sonst
   * leuchten Puls, Meilensteine und Lieferung alle drei auf demselben Pfad.
   */
  const istAktiv = (punkt: Punkt): boolean => {
    if (punkt.ohneAktivmarke) return false;
    const [ziel, frage] = punkt.ziel.split("?");
    if (frage) {
      const reiter = new URLSearchParams(frage).get("reiter");
      return pfad === ziel && suche["reiter"] === reiter;
    }
    if (ziel === "/") return pfad === "/";
    if (punkt.praefix) {
      return pfad === ziel || pfad.startsWith(ziel + "/");
    }
    return pfad === ziel;
  };

  const rolle = gewaehlt
    ? (ich.data?.projekte ?? []).find((p) => p.projektId === gewaehlt.id)?.rolle
    : undefined;

  return (
    <nav
      aria-label="Bereiche"
      className={cx(
        "flex w-[236px] flex-none flex-col bg-tafel text-tafel-tinte",
        className,
      )}
    >
      <div className="flex flex-none items-center gap-2.5 px-[22px] pt-[26px]">
        <Link to="/" aria-label="Zur Übersicht" className={cx(focusRing)}>
          <Wortmarke groesse={26} mitZeichen={false} aufDunkel />
        </Link>
      </div>

      {/*
        Der Kontextschalter steht in `entwurf/Kontextwaehler`. Tremors `Select`
        trug hier einen Rahmen und seinen eigenen Doppelpfeil — die Vorlage hat
        keinen Rahmen, ein Kürzelkästchen und einen einzelnen Chevron direkt
        hinter dem Namen. Radix liegt weiter darunter.
      */}
      <div className="flex-none px-[22px] pb-[22px] pt-[30px]">
        <Mikrolabel grad={11} ton="rubrik">
          Kontext
        </Mikrolabel>
        <Kontextwaehler
          projekte={projekte.data ?? []}
          auswahl={auswahl}
          alleWert={ALLE_PROJEKTE}
          onWaehlen={waehleProjekt}
        />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden pb-6 pt-[22px]">
        {/*
          `last:mb-0`: Die 26px trennen zwei Gruppen, sie hängen nicht hinter der
          letzten. Zusammen mit `pb-6` standen dort 50px, die nur dann sichtbar
          wurden, wenn die Liste ohnehin rollt — als Rollweg ohne Inhalt. Bei
          900px Fensterhöhe waren es genau sechs Pixel, die man ins Leere rollen
          konnte.
        */}
        {gruppen.map((gruppe) => (
          <div key={gruppe.titel} className="mb-[26px] last:mb-0">
            <div className="px-[22px] pb-2.5">
              <Mikrolabel grad={11} ton="rubrik">
                {gruppe.titel}
              </Mikrolabel>
            </div>
            <ul role="list">
              {gruppe.punkte.map((punkt) => {
                const aktiv = istAktiv(punkt);
                return (
                  <li key={punkt.label}>
                    <Link
                      to={punkt.ziel}
                      aria-current={aktiv ? "page" : undefined}
                      className={cx(
                        "relative flex h-[31px] items-center gap-2.5 px-[22px] transition-colors duration-150 ease-aus",
                        aktiv
                          ? "text-tafel-akzent"
                          : "text-tafel-tinte2 hover:bg-tafel-wisch",
                        focusRing,
                      )}
                    >
                      {/*
                        Die Kante sitzt am linken Rand der Schiene, nicht am
                        Text. Sie ist dekorativ — `aria-current` sagt dasselbe
                        für Vorleseprogramme.
                      */}
                      {aktiv ? (
                        <span
                          aria-hidden="true"
                          className="absolute inset-y-0 left-0 w-0.5 bg-tafel-akzent"
                        />
                      ) : null}
                      <span
                        className={cx(
                          "min-w-0 flex-1 truncate text-[14px]",
                          aktiv ? "font-medium" : "font-light",
                        )}
                      >
                        {punkt.label}
                      </span>
                      {punkt.zahl !== undefined && punkt.zahl !== null ? (
                        <Mikrolabel
                          grad={11}
                          zahl
                          className={
                            aktiv || (punkt.akzentAbEins && punkt.zahl > 0)
                              ? "text-tafel-akzent"
                              : "text-tafel-tinte3"
                          }
                        >
                          {punkt.zahl}
                        </Mikrolabel>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/*
        Der Fuß. Er zeigt die angemeldete Person — die Vorlage trug hier
        „Emma Stone" fest verdrahtet, das war Beispieldaten aus dem
        Tremor-Muster. Der Name kommt aus `personen`, weil `ich` nur Kennung,
        Rollen und Rechte kennt.

        Der Fuß ist gleichzeitig der Auslöser des Benutzermenüs (Radix), damit
        Abmelden und Thema erreichbar bleiben. Der Entwurf zeichnet nur die
        Zeile und sagt nichts darüber, wo das Menü hängt.
      */}
      <div className="mx-[22px] flex-none border-t border-tafel-linie pb-6 pt-[18px]">
        <DropdownUserProfile>
          <button
            type="button"
            aria-label="Benutzermenü"
            className={cx(
              "flex w-full items-center gap-[11px] text-left transition-colors duration-150 ease-aus hover:bg-tafel-wisch data-[state=open]:bg-tafel-wisch",
              focusRing,
            )}
          >
            <span
              aria-hidden="true"
              className="flex size-[27px] flex-none items-center justify-center bg-tafel-linie font-mono text-[10px] text-tafel-tinte"
            >
              {person.data ? initialen(person.data.name) : "—"}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-normal text-tafel-tinte">
                {person.data?.name ?? "Wird geladen …"}
              </span>
              <span className="block font-mono text-[10.5px] text-tafel-tinte3">
                {rolle ? ROLLENNAME[rolle] : "Angemeldet"}
              </span>
            </span>
          </button>
        </DropdownUserProfile>
      </div>
    </nav>
  );
}
